import camelcaseKeys from 'camelcase-keys'

import { LUKITA_URL } from '@/config'
import { ChatCreditsExhaustedError } from '@/modules/errors'
import {
  executeDelete,
  executeGet,
  executePatch,
  executePost,
  getHeaders,
} from '@/services/lukita'
import type {
  AgentName,
  ChatMessage,
  ChatSession,
  ChatSessionWithMessages,
} from '../types'

export async function fetchSessions(
  slug: string,
  { archived = false }: { archived?: boolean } = {},
): Promise<ChatSession[]> {
  // Pass archived through queryParams, not the route: executeGet always
  // appends `?<query>`, so a route that already had `?archived=true` became
  // `...chats?archived=true?`, leaving Rails with params[:archived] == "true?"
  // (not "true"), so it returned ACTIVE chats for the archived request.
  const response = await executeGet(`/projects/${slug}/chats`, {
    queryParams: archived ? { archived: 'true' } : {},
  })
  const payload = (await response.json()) as Array<Record<string, unknown>>
  return payload.map((entry) => camelcaseKeys(entry) as unknown as ChatSession)
}

export async function fetchSession(
  slug: string,
  id: string,
): Promise<ChatSessionWithMessages> {
  const response = await executeGet(`/projects/${slug}/chats/${id}`)
  const payload = (await response.json()) as Record<string, unknown> & {
    messages?: Array<Record<string, unknown>>
  }
  const session = camelcaseKeys(payload) as unknown as ChatSession & {
    activeTurnId?: string | null
    cancelledReportEditTokens?: string[]
  }
  const messages = (payload.messages ?? []).map(
    (message) => camelcaseKeys(message) as unknown as ChatMessage,
  )
  return {
    ...session,
    messages,
    activeTurnId: session.activeTurnId ?? null,
    cancelledReportEditTokens: session.cancelledReportEditTokens ?? [],
  }
}

interface CreateSessionOptions {
  title?: string | null
  agent?: AgentName
}

export async function createSession(
  slug: string,
  options: CreateSessionOptions = {},
): Promise<ChatSession> {
  const response = await executePost(`/projects/${slug}/chats`, {
    chat_session: { title: options.title, agent: options.agent },
  })
  const payload = (await response.json()) as Record<string, unknown>
  return camelcaseKeys(payload) as unknown as ChatSession
}

export async function renameSession(
  slug: string,
  id: string,
  title: string,
): Promise<ChatSession> {
  const response = await executePatch(`/projects/${slug}/chats/${id}`, {
    chat_session: { title },
  })
  const payload = (await response.json()) as Record<string, unknown>
  return camelcaseKeys(payload) as unknown as ChatSession
}

export async function archiveSession(slug: string, id: string): Promise<void> {
  await executeDelete(`/projects/${slug}/chats/${id}`)
}

export async function unarchiveSession(
  slug: string,
  id: string,
): Promise<ChatSession> {
  const response = await executePost(
    `/projects/${slug}/chats/${id}/unarchive`,
    {},
  )
  const payload = (await response.json()) as Record<string, unknown>
  return camelcaseKeys(payload) as unknown as ChatSession
}

export async function pinSession(
  slug: string,
  id: string,
): Promise<ChatSession> {
  const response = await executePost(`/projects/${slug}/chats/${id}/pin`, {})
  const payload = (await response.json()) as Record<string, unknown>
  return camelcaseKeys(payload) as unknown as ChatSession
}

export async function unpinSession(
  slug: string,
  id: string,
): Promise<ChatSession> {
  const response = await executeDelete(`/projects/${slug}/chats/${id}/pin`)
  const payload = (await response.json()) as Record<string, unknown>
  return camelcaseKeys(payload) as unknown as ChatSession
}

interface SendMessageOptions {
  confirmation?:
    | { kind: 'bulk_edit'; batchToken: string }
    | { kind: 'remove_transactions'; batchToken: string }
    | { kind: 'report_edit'; token: string }
    | { kind: 'report_edit'; tokens: string[] }
    | { kind: 'case_fact'; token: string }
    | null
}

function confirmationBody(
  confirmation: NonNullable<SendMessageOptions['confirmation']>,
): Record<string, unknown> {
  if ('batchToken' in confirmation) {
    return { kind: confirmation.kind, batch_token: confirmation.batchToken }
  }
  return confirmation
}

// Saves the user's cancel so a later apply, or Apply all, skips the card.
export async function cancelReportEdit(
  slug: string,
  token: string,
): Promise<void> {
  await executePost(`/projects/${slug}/report_edits/${token}/cancel`)
}

// Dispatches a turn. The POST returns immediately (202) once lukita has
// enqueued the streaming job; the turn's events arrive over the session's
// ChatChannel, not on this response. Returns the turn_id lukita assigned.
//
// No `accuracy_mode` is sent: the toggle was removed and daystrom forces
// High Accuracy for every turn (see daystrom `model_selection`).
export async function sendMessage(
  slug: string,
  id: string,
  message: string,
  { confirmation }: SendMessageOptions = {},
): Promise<{ turnId: string }> {
  const headers = await getHeaders({ 'Content-Type': 'application/json' })
  const body: Record<string, unknown> = {
    message,
  }
  if (confirmation) body.confirmation = confirmationBody(confirmation)
  const response = await fetch(
    `${LUKITA_URL}/projects/${slug}/chats/${id}/messages`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    },
  )

  if (!response.ok) {
    let payload: { error?: { code?: string; message?: string } } | null = null
    try {
      payload = await response.json()
    } catch {
      // non-JSON body — fall through to the generic error
    }
    const code = payload?.error?.code
    const message =
      payload?.error?.message ?? `Failed to send message: ${response.status}`
    if (code === ChatCreditsExhaustedError.CODE) {
      throw new ChatCreditsExhaustedError(
        message,
        response.status,
        (payload ?? {}) as Record<string, unknown>,
      )
    }
    throw new Error(message)
  }

  const payload = (await response.json()) as { turn_id?: string }
  return { turnId: payload.turn_id ?? '' }
}

// Requests cancellation of the in-flight turn for a session. lukita flags
// the streaming job, which unwinds the daystrom stream, persists the
// partial turn, and emits a terminal event over the channel.
export async function stopTurn(slug: string, id: string): Promise<void> {
  await executeDelete(`/projects/${slug}/chats/${id}/turn`)
}
